import { ChangeEvent, useEffect, useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Button, Card, IconButton, TextField } from "@mui/material"
import { useSelector } from "react-redux"
import { useAppDispatch, useAppSelector } from "../../../redux/store"
import { selectCurrentUser, selectCurrentUserCustomers } from "../../../redux/user"
import { selectCustomerNumber } from "../../../redux/customer"
import { getCorpWaterlineInventory } from "./services"
import CompliancePlip from "../../../components/CompliancePlip"
import InventoryButtons from "./inventory/InventoryButtons"
import WaterlineInventoryTable from "./inventory/WaterlineInventoryTable"
import { WaterlineInventoryItem } from "./types"
import { getFilteredInventoryItems } from "./utils/helpers"
import debounce from "../../../helpers/debounce"
import { ComplianceStatus } from "../../../types"
import { exportInventoryItems } from "./utils/exports"
import { addError } from "../../../redux/alerts"

type OpenInventoryAlert = {
  open: boolean,
  message: string,
}

const WaterlineInventory = () => {
  const currentUser = useAppSelector(selectCurrentUser)
  const dispatch = useAppDispatch()
  const customerNumber = useSelector(selectCustomerNumber)
  const groupCustomerNumbers = useAppSelector(selectCurrentUserCustomers)

  const [inventory, setInventory] = useState<WaterlineInventoryItem[] | null>(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('active')
  const [openAlert, setOpenAlert] = useState<OpenInventoryAlert>({open: false, message: ''})

  const getInventoryItems = async () => {
    try {
      const inventory = await getCorpWaterlineInventory(customerNumber, currentUser, groupCustomerNumbers)
      setInventory(inventory)
    } catch (err) {
      console.error(err)
      dispatch(addError('Failed to get inventory items'))
    }
  }

  useEffect(() => {
    if (customerNumber && currentUser && groupCustomerNumbers) {
      getInventoryItems()
    }
  }, [customerNumber, currentUser, groupCustomerNumbers])

  const filteredItems = useMemo(() => {
    return inventory
      ? getFilteredInventoryItems(search, statusFilter, inventory)
      : null
  }, [search, statusFilter, inventory])

  const handleExport = () => {
    if (inventory) {
      exportInventoryItems(inventory)
    }
  }

  const handleSearch = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const searchValue = event.target.value
    setSearch(searchValue)
  }

  const handleOpenAlert = (message: string) => {
    setOpenAlert({ open: true, message })
  }

  const handleCloseAlert = () => {
    setOpenAlert({ open: false, message: openAlert.message })
  }

  const debouncedHandleSearch = debounce(handleSearch, 50)

  const complianceStatuses = useMemo(() => {
    const initialStatus = { 1: 0, 2: 0, 3: 0, 4: 0 }

    return (inventory ?? []).reduce((acc: Record<number, number>, item) => {
      acc[item.status]++
      return acc
    }, initialStatus)
  }, [inventory])

  const complianceMessage = (status: number): string => {
    const statusMessages: Record<number, string> = {
      1: 'Valid',
      2: 'Expiring Soon',
      3: 'Expired',
      4: 'Inactive',
    };

    return statusMessages[status] || 'Inactive';
  }

  const complianceSummaries = [
    ComplianceStatus.Expired,
    ComplianceStatus.Expiring,
    ComplianceStatus.Ok,
    ComplianceStatus.Inactive,
  ]

  return (
    <>
      <Card sx={{ width: "100%" }} variant="outlined">
        <Box className="box-header flex flex-space-between">
          <Box>Waterline Inventory</Box>
          <Box className="flex">
            {
              complianceSummaries.map((status, index) => {
                return (
                  <Box key={`plip-container-${status}`}>
                    <CompliancePlip message={complianceMessage(status)} status={status} className="m-r-8" />
                    <span className={`${index < complianceSummaries.length - 1 ? 'm-r-12' : ''} muted-text`.trim()}>
                      { complianceStatuses[status] }
                    </span>
                  </Box>
                )
              })
            }
          </Box>
        </Box>
        <Box className="table-subheader flex flex-space-between">
          <Box>
            <TextField
              sx={{ minWidth: 200 }}
              size="small"
              label="Search"
              onChange={debouncedHandleSearch}
              InputProps={
                {
                  endAdornment: (
                    <IconButton>
                      <FontAwesomeIcon icon="magnifying-glass" size="sm" className="m-l-8" />
                    </IconButton>
                  )
                }
              }
            />
          </Box>
          <Box>
            <InventoryButtons statusFilter={statusFilter} setStatusFilter={setStatusFilter} />
          </Box>
        </Box>
        <Box sx={ { paddingBottom: 0 } } className="table-subheader flex flex-end">  
          <Button variant="contained" onClick={handleExport}>Export</Button>
        </Box>
        <Box sx={ { padding: '16px' } }>
          <WaterlineInventoryTable
            inventory={filteredItems}
            getInventoryItems={getInventoryItems}
            onAlert={handleOpenAlert}
          />
        </Box>
      </Card> 
    </>

  )
}

export default WaterlineInventory
